import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Bell } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Fresh Produce", "Eggs", "Dairy", "Meat", "Poultry",
  "Honey", "Baked Goods", "Preserved", "Flowers", "Other"
];

/**
 * Public "notify me when farms are available" modal. Anyone can submit —
 * requests are stored server-side; live email dispatch is gated by the
 * NOTIFY_LIVE_SEND flag so the workflow is fully exercised in preview
 * without emailing real customers yet.
 */
export default function NotifyMeModal({ open, onClose, defaultState = "", defaultZip = "" }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [zip, setZip] = useState(defaultZip || "");
  const [state, setState] = useState(defaultState ? defaultState.toUpperCase() : "");
  const [pickupOk, setPickupOk] = useState(true);
  const [cats, setCats] = useState([]);
  const [customRequest, setCustomRequest] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [coverageOk, setCoverageOk] = useState(true);
  const [emailErr, setEmailErr] = useState("");
  const [zipErr, setZipErr] = useState("");

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const ZIP_RE = /^\d{5}$/;
  const CUSTOM_MAX = 500;

  const toggleCat = (c) => {
    setCats(cs => cs.includes(c) ? cs.filter(x => x !== c) : [...cs, c]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setEmailErr(""); setZipErr("");
    let ok = true;
    if (!EMAIL_RE.test((email || "").trim())) { setEmailErr("Please enter a valid email address."); ok = false; }
    if (!ZIP_RE.test((zip || "").trim())) { setZipErr("Please enter a 5-digit US ZIP code."); ok = false; }
    if (!consent) { toast.error("Please agree to receive availability emails before submitting."); ok = false; }
    if (!ok) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/notify/interest`, {
        email: email.trim(),
        first_name: firstName || null,
        zip_code: zip.trim(),
        customer_state: state || null,
        pickup_ok: pickupOk,
        interested_categories: cats.length ? cats : (customRequest.trim() ? ["Other"] : null),
        custom_request: (customRequest || "").slice(0, CUSTOM_MAX).trim() || null,
        consent: consent === true,
      });
      toast.success(res.data.message);
      setCoverageOk(!!res.data.coverage_data_complete);
      setDone(true);
    } catch (e) {
      const detail = e.response?.data?.detail || "Couldn't save your request";
      toast.error(detail);
      // Surface a server-side ZIP rejection inline too.
      if (/zip/i.test(String(detail))) setZipErr(String(detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="notify-modal-backdrop"
    >
      <div
        className="bg-white w-full max-w-lg rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'Georgia, serif' }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(15,29,47,0.1)' }}>
          <div className="flex items-center gap-2">
            <Bell className="w-4 h-4" style={{ color: '#B6A46D' }} />
            <h3 className="text-base" style={{ color: '#0f1d2f' }} data-testid="notify-modal-title">Keep me posted</h3>
          </div>
          <button onClick={onClose} className="p-1" data-testid="notify-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(182, 164, 109, 0.15)' }}>
              <Bell className="w-6 h-6" style={{ color: '#B6A46D' }} />
            </div>
            <h4 className="text-lg mb-2" style={{ color: '#0f1d2f' }}>{coverageOk ? "You're on the list" : "Request saved"}</h4>
            <p className="text-sm leading-relaxed" style={{ color: '#0f1d2f', opacity: 0.7 }}>
              {coverageOk
                ? "We'll email you the moment farms matching your preferences come online. You can unsubscribe from any email."
                : "We saved your request, but we need a valid 5-digit ZIP and your state so we can check whether farms actually cover your area before we email you."}
            </p>
            <button
              onClick={onClose}
              className="mt-6 px-6 py-2.5 text-xs tracking-widest uppercase"
              style={{ backgroundColor: '#0f1d2f', color: 'white' }}
              data-testid="notify-modal-dismiss"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: '#0f1d2f', opacity: 0.75 }} data-testid="notify-modal-intro">
              We&rsquo;re welcoming independent farms to American Farmstand. Tell us
              where you are and what you&rsquo;d love to bring home. We&rsquo;ll let you know when
              participating farms are ready to serve your area.
            </p>
            <p className="text-xs" style={{ color: '#0f1d2f', opacity: 0.55 }}>
              Free to sign up. We only email you when a real farm match happens &mdash; never marketing spam. Unsubscribe anytime.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs tracking-widest uppercase block mb-1" style={{ color: '#0f1d2f', opacity: 0.6 }}>First name</span>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-3 py-2 border text-sm" style={{ borderColor: 'rgba(15, 29, 47, 0.2)' }} data-testid="notify-modal-firstname" />
              </label>
              <label className="block">
                <span className="text-xs tracking-widest uppercase block mb-1" style={{ color: '#0f1d2f', opacity: 0.6 }}>Email*</span>
                <input type="email" required value={email}
                  onChange={e => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
                  className="w-full px-3 py-2 border text-sm"
                  style={{ borderColor: emailErr ? '#8a2c22' : 'rgba(15, 29, 47, 0.2)' }}
                  data-testid="notify-modal-email" />
                {emailErr && (
                  <p className="text-xs mt-1" style={{ color: '#8a2c22' }} data-testid="notify-modal-email-error">{emailErr}</p>
                )}
              </label>
              <label className="block sm:col-span-2">
                <span className="text-xs tracking-widest uppercase block mb-1" style={{ color: '#0f1d2f', opacity: 0.6 }}>ZIP code* · State</span>
                <div className="flex gap-2">
                  <input type="text" required value={zip}
                    onChange={e => { setZip(e.target.value); if (zipErr) setZipErr(""); }}
                    maxLength={10} placeholder="12401"
                    className="w-40 px-3 py-2 border text-sm"
                    style={{ borderColor: zipErr ? '#8a2c22' : 'rgba(15, 29, 47, 0.2)' }}
                    data-testid="notify-modal-zip" />
                  <input type="text" value={state} onChange={e => setState(e.target.value.toUpperCase())} maxLength={2} placeholder="NY"
                    className="w-20 px-3 py-2 border text-sm uppercase" style={{ borderColor: 'rgba(15, 29, 47, 0.2)' }} data-testid="notify-modal-state" />
                </div>
                {zipErr ? (
                  <p className="text-xs mt-1" style={{ color: '#8a2c22' }} data-testid="notify-modal-zip-error">{zipErr}</p>
                ) : (
                  <p className="text-xs mt-1" style={{ color: '#0f1d2f', opacity: 0.55 }}>
                    Used to check whether farms serve your area.
                  </p>
                )}
              </label>
            </div>

            <div>
              <span className="text-xs tracking-widest uppercase block mb-2" style={{ color: '#0f1d2f', opacity: 0.6 }}>Interested in</span>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map(c => {
                  const active = cats.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCat(c)}
                      className={`px-3 py-1.5 text-xs border transition-all ${active ? "bg-[#0f1d2f] text-white border-[#0f1d2f]" : "bg-white text-[#0f1d2f] border-[#0f1d2f]/30 hover:border-[#0f1d2f]"}`}
                      data-testid={`notify-modal-cat-${c.toLowerCase().replace(/\s+/g,'-')}`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional free-text write-in. Plain text, 500-char cap. We
                never auto-convert this into a category or auto-trigger
                alerts; it's stored alongside the request for context. */}
            <label className="block">
              <span className="text-xs tracking-widest uppercase block mb-1" style={{ color: '#0f1d2f', opacity: 0.6 }}>
                Anything else you&rsquo;re looking for?
              </span>
              <textarea
                value={customRequest}
                onChange={(e) => setCustomRequest(e.target.value.slice(0, CUSTOM_MAX))}
                maxLength={CUSTOM_MAX}
                rows={2}
                placeholder="Duck eggs, sourdough, goat cheese…"
                className="w-full px-3 py-2 border text-sm"
                style={{ borderColor: 'rgba(15, 29, 47, 0.2)', resize: 'vertical' }}
                data-testid="notify-modal-custom-request"
              />
              <div className="flex justify-between text-xs mt-1" style={{ color: '#0f1d2f', opacity: 0.55 }}>
                <span>Optional. Save specific items you&rsquo;d like to find — we&rsquo;ll keep them with your request. Custom text doesn&rsquo;t trigger automatic availability alerts.</span>
                <span data-testid="notify-modal-custom-count">{customRequest.length}/{CUSTOM_MAX}</span>
              </div>
            </label>

            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: '#0f1d2f' }}>
              <input type="checkbox" checked={pickupOk} onChange={e => setPickupOk(e.target.checked)}
                style={{ width: "auto", padding: 0, flexShrink: 0 }} data-testid="notify-modal-pickup-ok" />
              I'm open to local pickup when a farm nearby offers it
            </label>

            <label className="flex items-start gap-2 text-sm cursor-pointer" style={{ color: '#0f1d2f' }}>
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                className="mt-0.5" style={{ width: "auto", padding: 0, flexShrink: 0 }} data-testid="notify-modal-consent" />
              <span>Email me when a farm match is available. Unsubscribe anytime.</span>
            </label>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 text-xs tracking-widest uppercase border" style={{ borderColor: 'rgba(15,29,47,0.3)', color: '#0f1d2f' }}>Cancel</button>
              <button type="submit" disabled={submitting || !consent} className="px-6 py-2 text-xs tracking-widest uppercase disabled:opacity-50" style={{ backgroundColor: '#0f1d2f', color: 'white' }} data-testid="notify-modal-submit">
                {submitting ? "Saving…" : "Keep me posted"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
